'use client';

import Link from '@eliadmin/core/src/components/link';
import React, { useEffect, useState } from 'react';

import { MdKeyboardArrowDown } from 'react-icons/md';

import type { ImageType } from '@eliadmin/core/src/api/api-types';
import type { Faq } from '@eliadmin/core/src/api/faqs/faqs-api';
import type { TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';

import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { CategoryCard } from '@eliadmin/core/src/components/categories/category-card';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { Button } from '../ui/button';
import { useInfiniteQuery } from '@tanstack/react-query';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { useSearchParams } from 'next/navigation';
import { CATEGORY_GRID_PRODUCT_IMAGE_SIZES, ProductCard } from '../product/product-card';
import { CategoryFilterSheet } from '@eliadmin/core/src/components/categories/category-filter-sheet';
import CategorySortToggle from '@eliadmin/core/src/components/categories/category-sort-toggle';
import { Inspiration } from '../homepage/inspiration';
import { FAQ } from '../homepage/faq';
import { Reviews } from '../homepage/reviews';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { CategoryProductsSkeleton } from './category-products-skeleton';

export type Category = {
  data?: Category[];
  childs: Category[];
  name: string;
  description: string;
  short_description: string;
  slug: string;
  icon?: string;
  image?: ImageType;
};

type CategoriesPageNewProps = {
  categories: Category[];
  locale: string;
  faq: Faq[];
  inspirations?: TDynamicData[];
};

export const CategoriesPageNew = ({ categories, locale, faq, inspirations }: CategoriesPageNewProps) => {
  const isDesktop = useBreakpoint('sm');
  const { t } = useTranslations();
  const [visibleCount, setVisibleCount] = useState(5);
  const { me, isInitialized } = useCustomerStore();

  const searchParams = useSearchParams();

  const parsedPage = Number(searchParams.get('page') ?? '1');
  const currentPage = Number.isSafeInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1;

  const handleShowMore = () => {
    setVisibleCount(prev => prev + 5);
  };

  const filters = useCategoryStore(s => s.filter);

  const queryWithoutPage = buildProductsQuery({
    manufacturers: filters.manufacturers ?? [],
    results: 16,
    order: filters.sort.order,
    orderBy: filters.sort.orderBy,
    attributes: filters.attributes,
    price_from: filters.price_min,
    price_to: filters.price_max,
    availabilities: filters.availabilities,
    suppliers: filters.suppliers,
  });
  const productResetUri = buildProductsRequest({ ...queryWithoutPage, page: undefined }).uri;
  const [settledFilterUri, setSettledFilterUri] = useState<string | null>(null);
  const isFilterResetPending = settledFilterUri !== null && settledFilterUri !== productResetUri;

  const visibleCategories = isDesktop ? categories : categories.slice(0, visibleCount);
  const hasMore = !isDesktop && visibleCount < categories.length;

  const productsQuery = useInfiniteQuery({
    queryKey: [
      CacheKey.PRODUCTS,
      'infinite',
      currentPage,
      productResetUri,
      locale,
      me?.customer.id ?? 'guest',
    ],
    queryFn: ({ pageParam }) => {
      return fetchProducts({
        query: {
          ...queryWithoutPage,
          page: pageParam,
        },
        locale,
        authToken: me?.authToken,
      });
    },
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: currentPage,
    enabled: isInitialized && !isFilterResetPending,
  });

  useEffect(() => {
    // Establish the baseline only after the initial (possibly URL-hydrated) list
    // loads. A bookmarked page must not reset merely because the component mounts.
    if (settledFilterUri === null) {
      if (productsQuery.data) setSettledFilterUri(productResetUri);
      return;
    }
    if (!isFilterResetPending) return;
    if (currentPage !== 1) {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
      return;
    }
    // Wait for the page reset before enabling a request with the new filters.
    setSettledFilterUri(productResetUri);
  }, [currentPage, isFilterResetPending, productResetUri, productsQuery.data, settledFilterUri]);

  const flatList = productsQuery.data?.pages.flatMap(page => page.data) ?? [];

  const isLoadingProducts = !isInitialized || productsQuery.isPending || !productsQuery.data;

  return (
    <>
      <section
        className={cn(
          'custom-section',
          'gap-6 pb-12 pt-12 sm:gap-8 sm:pt-16 md:gap-10 lg:gap-12 lg:pt-20',
        )}
      >
        <div className={cn('custom-container', 'flex flex-col items-start justify-start gap-6')}>
          <Link
            prefetch={false}
            href={ROUTES.HOME()}
            className="text-sm text-primary transition-all hover:text-primary/90 sm:text-base"
          >
            {t('text160')}
          </Link>
          <h1 className="text-4xl font-medium tracking-tight sm:text-5xl">{t('text2309')}</h1>
          <p className="text-sm font-light text-black/70 sm:text-base">{t('text2353')}</p>
        </div>
        <div className={cn('custom-container', 'relative')}>
          <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 md:gap-1 lg:grid-cols-3 xl:gap-2">
            {visibleCategories.map(category => (
              <CategoryCard category={category} key={category.slug} />
            ))}
          </div>
          {hasMore && (
            <div className="relative w-full">
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-[50px] bg-gradient-to-t from-white to-transparent" />
              <div className="flex justify-start">
                <div
                  onClick={handleShowMore}
                  className={cn('flex cursor-pointer items-center justify-start gap-2')}
                >
                  <Button type="button" size={'icon'} variant={'alternative-default'} aria-label={t('text2354')}>
                    <MdKeyboardArrowDown size={18} className="text-primary" />
                  </Button>
                  <p className="text-sm tracking-tight text-primary">{t('text2354')}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      <section className={cn('custom-section', 'pb-12 sm:pb-16 md:pb-20 lg:pb-24')}>
        {isLoadingProducts || !productsQuery.data ? (
          <CategoryProductsSkeleton />
        ) : (
          <div
            className={cn('custom-container', 'flex flex-col items-center justify-center gap-6')}
          >
            <div className="flex w-full flex-col items-center justify-center gap-4 sm:gap-6 md:flex-row md:justify-between">
              <p className="text-sm text-[#636363] lg:text-base">
                {productsQuery.data.pages[0].meta.total} {t('text173')}
              </p>
              <CategorySortToggle />
            </div>
            <div className="grid w-full grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4 xl:gap-3">
              {flatList.map(product => (
                <ProductCard
                  product={product}
                  key={product.id}
                  imageSizes={CATEGORY_GRID_PRODUCT_IMAGE_SIZES}
                />
              ))}
            </div>
            <div className="flex w-full flex-col items-center justify-center gap-6">
              <div className="flex w-full flex-col items-center justify-center gap-2">
                <p className="text-center text-sm sm:text-base">
                  {flatList.length} / {productsQuery.data.pages[0].meta.total}
                </p>
                <div className="flex h-[6px] w-full max-w-[300px] items-center justify-start bg-black/5">
                  <div
                    className="h-full bg-primary"
                    style={{
                      width: `${(flatList.length / productsQuery.data.pages[0].meta.total) * 100}%`,
                    }}
                  />
                </div>
              </div>
              {!!productsQuery.hasNextPage && (
                <div
                  className={cn('flex flex-row justify-center', 'ea-category-products__load-more')}
                >
                  <Button
                    variant="outline"
                    isLoading={productsQuery.isFetchingNextPage}
                    onClick={() => productsQuery.fetchNextPage()}
                  >
                    {t('text338')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </section>
      <Inspiration initialData={inspirations} initialLocale={locale} />
      <FAQ faq={faq} />
      {/* <Reviews /> */}
    </>
  );
};
